import React, { useState, useRef, useEffect, useCallback } from 'react';
import { makeSvgResponsive } from '../utils/fabricSvg';
import {
  Square,
  Circle,
  Triangle,
  Minus,
  ArrowRight,
  Trash2,
  X,
  PenTool,
  Undo2,
  Redo2,
  Image as ImageIcon,
  Sliders,
  MousePointer,
  Eraser,
} from 'lucide-react';

// ============================================================
// InteractiveContentTools — Fabric.js powered drawing canvas
// ============================================================

let fabricModule = null;

const loadFabric = async () => {
  if (fabricModule) return fabricModule;
  fabricModule = await import('fabric');
  return fabricModule;
};

// Pre-load Fabric immediately on module evaluation so it's cached
// before any mouse events fire. This eliminates the async gap that
// caused shapes to be created after the event cycle completed.
loadFabric();

// Ensures freeDrawingBrush exists on the canvas.
// Fabric.js v6 does NOT auto-create a brush — we must instantiate PencilBrush.
const ensureBrush = (canvas, fabric) => {
  if (!canvas) return null;
  if (!canvas.freeDrawingBrush) {
    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
  }
  return canvas.freeDrawingBrush;
};

// 16:9, matching the slide the drawing is destined for, so what you compose
// here is what lands on the slide. The old 800x500 (1.6:1) meant a drawing had
// to be re-fitted into a differently shaped box on the way in.
//
// 768 wide is chosen to fit the modal without scrolling: the dialog is
// max-w-5xl (1024px) less its 208px sidebar and padding, leaving ~784px.
const CANVAS_WIDTH = 768;
const CANVAS_HEIGHT = 432;

const TOOLS = [
  { id: 'select', label: 'Select', icon: MousePointer },
  { id: 'rect', label: 'Rectangle', icon: Square },
  { id: 'ellipse', label: 'Ellipse', icon: Circle },
  { id: 'triangle', label: 'Triangle', icon: Triangle },
  { id: 'line', label: 'Line', icon: Minus },
  { id: 'arrow', label: 'Arrow', icon: ArrowRight },
  { id: 'draw', label: 'Pen', icon: PenTool },
  { id: 'eraser', label: 'Eraser', icon: Eraser },
];

// ----------------------------------------------------------------
// Synchronous shape factory — no async, no await
// ----------------------------------------------------------------
// Uses the cached fabricModule directly. The module is guaranteed
// to be loaded by the time the canvas initializes (loadFabric()
// is called eagerly at module scope and again in the init effect).
// ----------------------------------------------------------------

const buildArrowPath = (startX, startY, endX, endY) => {
  const headLength = 15;
  const headAngle = Math.PI / 7;
  const angle = Math.atan2(endY - startY, endX - startX);

  const hx1 = endX - headLength * Math.cos(angle - headAngle);
  const hy1 = endY - headLength * Math.sin(angle - headAngle);
  const hx2 = endX - headLength * Math.cos(angle + headAngle);
  const hy2 = endY - headLength * Math.sin(angle + headAngle);

  return [
    ['M', startX, startY],
    ['L', endX, endY],
    ['M', endX, endY],
    ['L', hx1, hy1],
    ['M', endX, endY],
    ['L', hx2, hy2],
  ];
};

const createShape = (type, params) => {
  const fabric = fabricModule;
  if (!fabric) return null;

  const { left, top, width, height, startX, startY, endX, endY, fillColor, strokeColor, strokeWidth } = params;

  switch (type) {
    case 'rect':
      return new fabric.Rect({
        left, top, width, height,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth,
      });

    case 'ellipse':
      return new fabric.Ellipse({
        left, top,
        rx: width / 2,
        ry: height / 2,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth,
      });

    case 'triangle':
      return new fabric.Triangle({
        left, top, width, height,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth,
      });

    case 'line':
      return new fabric.Line(
        [startX, startY, endX, endY],
        { stroke: strokeColor, strokeWidth, fill: 'transparent' }
      );

    case 'arrow':
      return new fabric.Path(buildArrowPath(startX, startY, endX, endY), {
        stroke: strokeColor,
        strokeWidth,
        fill: '',
        selectable: true,
        evented: true,
      });

    default:
      return null;
  }
};

const InteractiveContentTools = ({ isVisible, onClose, onInsertContent }) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [activeTool, setActiveTool] = useState('select');
  const [fillColor, setFillColor] = useState('#6366f1');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [brushSize, setBrushSize] = useState(4);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [objectCount, setObjectCount] = useState(0);
  const [bgImage, setBgImage] = useState(null);
  const [bgOpacity, setBgOpacity] = useState(100);
  const [isDrawover, setIsDrawover] = useState(false);
  const isUpdatingRef = useRef(false);
  const shapeDrawRef = useRef({
    isDrawing: false,
    startPoint: null,
    activeShape: null,
  });

  // Initialize Fabric.js canvas
  useEffect(() => {
    if (!isVisible) return;

    let canvas = null;
    let mounted = true;

    const init = async () => {
      const fabric = await loadFabric();
      if (!mounted || !canvasRef.current) return;

      // Clean up existing canvas
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }

      canvas = new fabric.Canvas(canvasRef.current, {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: '#ffffff',
        selection: true,
      });

      // Ensure Fabric's internal dimensions match the element
      canvas.setWidth(CANVAS_WIDTH);
      canvas.setHeight(CANVAS_HEIGHT);

      // Fabric v6: brush must be created explicitly before any drawing-mode use
      ensureBrush(canvas, fabric);

      fabricCanvasRef.current = canvas;

      // Save initial state
      saveHistory(canvas);

      // Track object count changes
      const updateCount = () => {
        if (mounted) setObjectCount(canvas.getObjects().length);
      };
      canvas.on('object:added', () => { updateCount(); saveHistory(canvas); });
      canvas.on('object:removed', () => { updateCount(); saveHistory(canvas); });
      canvas.on('object:modified', () => { saveHistory(canvas); });
      canvas.on('path:created', () => { updateCount(); saveHistory(canvas); });
    };

    init();

    return () => {
      mounted = false;
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
      setHistory([]);
      setHistoryIndex(-1);
      setObjectCount(0);
      setBgImage(null);
      setIsDrawover(false);
      setBgOpacity(100);
      setActiveTool('select');
    };
  }, [isVisible]);

  // Save canvas state for undo/redo
  const saveHistory = useCallback((canvas) => {
    if (isUpdatingRef.current) return;
    if (!canvas) return;

    let json;
    try {
      json = JSON.stringify(canvas.toJSON());
    } catch (error) {
      console.error('Failed to serialize canvas history state', error);
      return;
    }

    setHistory((prev) => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [...prev];
      if (base.length > 0 && base[base.length - 1] === json) return prev;
      const updated = [...base, json];
      if (updated.length > 50) updated.shift();
      setHistoryIndex(updated.length - 1);
      return updated;
    });
  }, [historyIndex]);

  // Undo
  const handleUndo = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || history.length <= 1) return;

    const newIndex = Math.max(0, historyIndex - 1);
    if (newIndex === historyIndex) return;

    isUpdatingRef.current = true;
    const snapshot = history[newIndex];
    if (typeof snapshot !== 'string') {
      console.warn('Undo snapshot missing or invalid at index', newIndex);
      isUpdatingRef.current = false;
      return;
    }

    let parsedSnapshot;
    try {
      parsedSnapshot = JSON.parse(snapshot);
    } catch (error) {
      console.error('Failed to parse undo snapshot', error);
      isUpdatingRef.current = false;
      return;
    }

    canvas.loadFromJSON(parsedSnapshot, () => {
      canvas.renderAll();
      setHistoryIndex(newIndex);
      setObjectCount(canvas.getObjects().length);
      isUpdatingRef.current = false;
    });
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(async () => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || historyIndex >= history.length - 1) return;

    const newIndex = historyIndex + 1;
    isUpdatingRef.current = true;
    const snapshot = history[newIndex];
    if (typeof snapshot !== 'string') {
      console.warn('Redo snapshot missing or invalid at index', newIndex);
      isUpdatingRef.current = false;
      return;
    }

    let parsedSnapshot;
    try {
      parsedSnapshot = JSON.parse(snapshot);
    } catch (error) {
      console.error('Failed to parse redo snapshot', error);
      isUpdatingRef.current = false;
      return;
    }

    canvas.loadFromJSON(parsedSnapshot, () => {
      canvas.renderAll();
      setHistoryIndex(newIndex);
      setObjectCount(canvas.getObjects().length);
      isUpdatingRef.current = false;
    });
  }, [history, historyIndex]);

  // Tool changes — synchronous where possible, async only for brush init
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Synchronously disable drawing mode for shape tools
    if (activeTool !== 'draw' && activeTool !== 'eraser') {
      canvas.isDrawingMode = false;
    }

    if (activeTool === 'select') {
      canvas.selection = true;
      canvas.defaultCursor = 'default';
    } else if (activeTool !== 'draw' && activeTool !== 'eraser') {
      canvas.selection = false;
      canvas.defaultCursor = 'crosshair';
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    }

    // Async only needed for brush setup (pen/eraser)
    if (activeTool === 'draw' || activeTool === 'eraser') {
      const setupBrush = async () => {
        const fabric = await loadFabric();
        const brush = ensureBrush(canvas, fabric);
        if (!brush) return;

        if (activeTool === 'draw') {
          canvas.isDrawingMode = true;
          brush.color = strokeColor;
          brush.width = brushSize;
        } else {
          canvas.isDrawingMode = true;
          brush.color = '#ffffff';
          brush.width = brushSize * 3;
        }
      };
      setupBrush();
    }
  }, [activeTool, strokeColor, brushSize]);

  // Update brush when colors/size change during drawing
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvas.isDrawingMode) return;

    const updateBrush = async () => {
      const fabric = await loadFabric();
      const brush = ensureBrush(canvas, fabric);
      if (!brush) return;
      if (activeTool === 'draw') {
        brush.color = strokeColor;
        brush.width = brushSize;
      }
    };

    updateBrush();
  }, [strokeColor, brushSize, activeTool]);

  const updateArrowPath = useCallback((arrowPath, startPoint, pointer) => {
    arrowPath.set({
      path: buildArrowPath(startPoint.x, startPoint.y, pointer.x, pointer.y),
      dirty: true,
    });
    arrowPath.setCoords();
  }, []);

  // Shape creation flow: create on down, update on move, finalize on up
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    const drawState = shapeDrawRef.current;
    if (!canvas) return;
    if (activeTool === 'select' || activeTool === 'draw' || activeTool === 'eraser') return;

    const onMouseDown = (opt) => {
      canvas.isDrawingMode = false;
      const pointer = canvas.getPointer(opt.e);
      drawState.startPoint = { x: pointer.x, y: pointer.y };
      drawState.isDrawing = true;

      const shape = createShape(activeTool, {
        left: pointer.x,
        top: pointer.y,
        width: 1,
        height: 1,
        startX: pointer.x,
        startY: pointer.y,
        endX: pointer.x,
        endY: pointer.y,
        fillColor,
        strokeColor,
        strokeWidth,
      });

      if (!shape) {
        drawState.isDrawing = false;
        drawState.startPoint = null;
        return;
      }

      shape.set({
        selectable: false,
        evented: false,
        objectCaching: false,
      });

      canvas.add(shape);
      drawState.activeShape = shape;
      canvas.requestRenderAll();
    };

    const onMouseMove = (opt) => {
      const { isDrawing, startPoint, activeShape } = drawState;
      if (!isDrawing || !startPoint || !activeShape) return;

      const pointer = canvas.getPointer(opt.e);
      const minX = Math.min(startPoint.x, pointer.x);
      const minY = Math.min(startPoint.y, pointer.y);
      const width = Math.max(Math.abs(pointer.x - startPoint.x), 1);
      const height = Math.max(Math.abs(pointer.y - startPoint.y), 1);

      if (activeTool === 'rect' || activeTool === 'triangle') {
        activeShape.set({
          left: minX,
          top: minY,
          width,
          height,
        });
      } else if (activeTool === 'ellipse') {
        activeShape.set({
          left: minX,
          top: minY,
          rx: width / 2,
          ry: height / 2,
        });
      } else if (activeTool === 'line') {
        activeShape.set({
          x1: startPoint.x,
          y1: startPoint.y,
          x2: pointer.x,
          y2: pointer.y,
        });
      } else if (activeTool === 'arrow') {
        updateArrowPath(activeShape, startPoint, pointer);
      }

      activeShape.set('dirty', true);
      activeShape.setCoords();
      canvas.requestRenderAll();
    };

    const onMouseUp = (opt) => {
      const { activeShape } = drawState;
      if (!activeShape) return;

      onMouseMove(opt);

      activeShape.set({
        selectable: true,
        evented: true,
      });
      activeShape.setCoords();
      canvas.setActiveObject(activeShape);
      canvas.requestRenderAll();

      drawState.isDrawing = false;
      drawState.startPoint = null;
      drawState.activeShape = null;
    };

    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);

    return () => {
      canvas.off('mouse:down', onMouseDown);
      canvas.off('mouse:move', onMouseMove);
      canvas.off('mouse:up', onMouseUp);
      drawState.isDrawing = false;
      drawState.startPoint = null;
      drawState.activeShape = null;
    };
  }, [activeTool, fillColor, strokeColor, strokeWidth, updateArrowPath]);

  // Background image loading
  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setBgImage(dataUrl);
      setIsDrawover(true);

      const fabric = await loadFabric();
      const imgEl = new window.Image();
      imgEl.onload = () => {
        const scale = Math.min(
          CANVAS_WIDTH / imgEl.width,
          CANVAS_HEIGHT / imgEl.height
        );
        const scaledWidth = imgEl.width * scale;
        const scaledHeight = imgEl.height * scale;
        const left = (CANVAS_WIDTH - scaledWidth) / 2;
        const top = (CANVAS_HEIGHT - scaledHeight) / 2;
        const fabricImg = new fabric.FabricImage(imgEl, {
          left,
          top,
          scaleX: scale,
          scaleY: scale,
          opacity: bgOpacity / 100,
          selectable: false,
          evented: false,
        });
        canvas.backgroundImage = fabricImg;
        canvas.requestRenderAll();
      };
      imgEl.src = dataUrl;
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [bgOpacity]);

  // Update background opacity
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !canvas.backgroundImage) return;
    canvas.backgroundImage.set({ opacity: bgOpacity / 100 });
    canvas.requestRenderAll();
  }, [bgOpacity]);

  // Delete selected objects
  const handleDeleteSelected = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObjects();
    if (active.length === 0) return;
    active.forEach(obj => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
  }, []);

  // Clear all
  const handleClearAll = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;
    canvas.clear();
    canvas.backgroundColor = '#ffffff';
    if (isDrawover && bgImage) {
      const imgEl = new window.Image();
      imgEl.onload = async () => {
        const fabric = await loadFabric();
        const scale = Math.min(CANVAS_WIDTH / imgEl.width, CANVAS_HEIGHT / imgEl.height);
        const scaledWidth = imgEl.width * scale;
        const scaledHeight = imgEl.height * scale;
        const left = (CANVAS_WIDTH - scaledWidth) / 2;
        const top = (CANVAS_HEIGHT - scaledHeight) / 2;
        const fabricImg = new fabric.FabricImage(imgEl, {
          left,
          top,
          scaleX: scale,
          scaleY: scale,
          opacity: bgOpacity / 100,
          selectable: false,
          evented: false,
        });
        canvas.backgroundImage = fabricImg;
        canvas.requestRenderAll();
      };
      imgEl.src = bgImage;
    } else {
      canvas.requestRenderAll();
    }
  }, [isDrawover, bgImage, bgOpacity]);

  // Export as PNG data URL
  const exportAsPng = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL({ format: 'png', quality: 1 });
  }, []);

  // Insert into slide
  const handleInsert = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    if (isDrawover && bgImage) {
      const dataUrl = exportAsPng();
      if (dataUrl && onInsertContent) {
        onInsertContent(dataUrl, {
          type: 'image_drawover',
          drawoverData: null,
          backgroundImage: bgImage,
          backgroundOpacity: bgOpacity,
        });
      }
    } else {
      // fabric serializes at the builder's fixed canvas size, which would drop
      // a hard 800x500 picture into the slide and overflow it. Rewrite the root
      // <svg> so the artwork scales to the slide and keeps its proportions.
      const svgString = makeSvgResponsive(canvas.toSVG());
      if (onInsertContent) {
        onInsertContent(svgString, { type: 'drawing' });
      }
    }
  }, [isDrawover, bgImage, bgOpacity, onInsertContent, exportAsPng]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-slate-700 to-slate-800">
          <div>
            <h2 className="text-base font-semibold text-white">
              {isDrawover ? 'Image Draw-Over Tool' : 'Drawing Canvas'}
            </h2>
            <p className="text-[11px] text-slate-300">
              {isDrawover
                ? 'Draw annotations on your uploaded image'
                : 'Add shapes, draw freehand, and create visual content'}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors p-1">
            <X size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-200 bg-gray-50 flex-wrap">
          {/* Shape tools */}
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeTool === tool.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
                title={tool.label}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{tool.label}</span>
              </button>
            );
          })}

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Colors */}
          <div className="flex items-center gap-1">
            <label className="text-[10px] text-gray-500 font-medium">Fill</label>
            <input
              type="color"
              value={fillColor}
              onChange={(e) => setFillColor(e.target.value)}
              className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-[10px] text-gray-500 font-medium">Stroke</label>
            <input
              type="color"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-[10px] text-gray-500 font-medium">Width</label>
            <input
              type="number"
              min="1"
              max="20"
              value={strokeWidth}
              onChange={(e) => setStrokeWidth(Number(e.target.value))}
              className="w-12 px-1.5 py-1 border border-gray-300 rounded text-xs"
            />
          </div>

          {(activeTool === 'draw' || activeTool === 'eraser') && (
            <div className="flex items-center gap-1">
              <label className="text-[10px] text-gray-500 font-medium">Brush</label>
              <input
                type="range"
                min="1"
                max="40"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-20"
              />
              <span className="text-[10px] text-gray-500 w-6 text-right">{brushSize}</span>
            </div>
          )}

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Undo / Redo */}
          <button
            onClick={handleUndo}
            disabled={historyIndex <= 0}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Undo"
          >
            <Undo2 size={14} />
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            title="Redo"
          >
            <Redo2 size={14} />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          {/* Delete / Clear */}
          <button
            onClick={handleDeleteSelected}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-all"
            title="Delete selected"
          >
            <Trash2 size={13} />
            <span className="hidden sm:inline">Delete</span>
          </button>
          <button
            onClick={handleClearAll}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all"
            title="Clear all"
          >
            <Eraser size={13} />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </div>

        {/* Canvas area */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 p-4 bg-gray-100 overflow-auto flex items-start justify-center">
            <div className="border-2 border-gray-300 rounded-xl shadow-inner bg-white" style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}>
              <canvas ref={canvasRef} />
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-52 border-l border-gray-200 bg-white overflow-y-auto">
            <div className="p-3 space-y-4">
              {/* Image draw-over section */}
              <div>
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Image Draw-Over</h3>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 transition-all text-xs font-medium"
                >
                  <ImageIcon size={14} />
                  {bgImage ? 'Replace Image' : 'Upload Image'}
                </button>
                {bgImage && (
                  <div className="mt-2 space-y-2">
                    <div className="w-full h-16 rounded-lg border border-gray-200 overflow-hidden">
                      <img src={bgImage} alt="Background" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-600 flex items-center gap-1">
                        <Sliders size={10} />
                        Image Opacity: {bgOpacity}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={bgOpacity}
                        onChange={(e) => setBgOpacity(Number(e.target.value))}
                        className="w-full mt-1"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setBgImage(null);
                        setIsDrawover(false);
                        const canvas = fabricCanvasRef.current;
                        if (canvas) {
                          canvas.backgroundImage = null;
                          canvas.requestRenderAll();
                        }
                      }}
                      className="w-full text-[10px] text-red-500 hover:text-red-700 font-medium"
                    >
                      Remove background image
                    </button>
                    <p className="text-[9px] text-gray-400 leading-tight">
                      The image is a read-only background. Your drawings appear above it.
                      Inserting creates a new <strong>Image Draw-Over</strong> block (original images are untouched).
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Canvas Info</h3>
                <p className="text-xs text-gray-600">{objectCount} object{objectCount !== 1 ? 's' : ''} on canvas</p>
                <p className="text-xs text-gray-400 mt-1">
                  {isDrawover ? 'Mode: Image Draw-Over' : 'Mode: Free Drawing'}
                </p>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Export Format</h3>
                <p className="text-[10px] text-gray-500 leading-tight">
                  {isDrawover
                    ? 'Flattened as PNG — preserves exact visual fidelity of drawing + background composite. The drawing is inserted as a new image draw-over block.'
                    : 'Exported as inline SVG — vector shapes remain crisp at any size. Inserted as an HTML code block.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500">
            {objectCount} element{objectCount !== 1 ? 's' : ''}
            {isDrawover ? ' • Image draw-over mode' : ''}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              disabled={objectCount === 0 && !isDrawover}
            >
              Insert into Slide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveContentTools;
